/**
 * CCOS Person Tracker · CDP Fase 1
 * ─────────────────────────────────
 * Gera cookie `fc_visitor_id` na primeira visita, dispara pageview em toda
 * mudança de rota (respeitando SPA), e expõe métodos:
 *
 *   personTracker.identify({ email, user_id, display_name, company, role })
 *   personTracker.track(eventType, { ref_id, title, metadata, ... })
 *
 * Respeita LGPD: consultamos o localStorage `cookie_consent` — se o usuário
 * ainda não deu consentimento, ficamos em modo "essencial" e NÃO rastreamos
 * comportamento (só ecossistema de session_id em memória).
 */

import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || '';
const VISITOR_KEY = 'fc_visitor_id';
const IDENTITY_KEY = 'fc_identity_v1';
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365; // 1 ano

function _uuid() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

function _getCookie(name) {
  const m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
  return m ? decodeURIComponent(m[1]) : null;
}

function _setCookie(name, value, maxAge = COOKIE_MAX_AGE) {
  const attrs = [
    `${name}=${encodeURIComponent(value)}`,
    `path=/`,
    `max-age=${maxAge}`,
    `SameSite=Lax`,
  ];
  if (window.location.protocol === 'https:') attrs.push('Secure');
  document.cookie = attrs.join('; ');
}

function _hasConsent() {
  try {
    // Se o app não implementa banner ainda, consideramos "opt-in implícito
    // pela ToS" — mesmo modelo do RD/GA. Trocar para checar cookie_consent
    // quando o banner LGPD estiver ativo.
    const raw = localStorage.getItem('cookie_consent');
    if (!raw) return true;
    const parsed = JSON.parse(raw);
    return parsed?.analytics !== false;
  } catch { return true; }
}

function _visitorId() {
  let v = _getCookie(VISITOR_KEY) || localStorage.getItem(VISITOR_KEY);
  if (!v) {
    v = _uuid();
    try { localStorage.setItem(VISITOR_KEY, v); } catch {}
  }
  if (_hasConsent() && !_getCookie(VISITOR_KEY)) _setCookie(VISITOR_KEY, v);
  return v;
}

function _getIdentity() {
  try { return JSON.parse(localStorage.getItem(IDENTITY_KEY) || '{}'); }
  catch { return {}; }
}

function _saveIdentity(patch) {
  try {
    const cur = _getIdentity();
    localStorage.setItem(IDENTITY_KEY, JSON.stringify({ ...cur, ...patch }));
  } catch {}
}

function _getUtm() {
  try {
    const p = new URLSearchParams(window.location.search);
    return {
      utm_source: p.get('utm_source') || undefined,
      utm_medium: p.get('utm_medium') || undefined,
      utm_campaign: p.get('utm_campaign') || undefined,
    };
  } catch { return {}; }
}

async function identify(payload = {}) {
  if (!_hasConsent()) return null;
  const body = {
    visitor_id: _visitorId(),
    ..._getIdentity(),
    ...payload,
  };
  _saveIdentity(payload);
  try {
    const r = await axios.post(`${API}/api/tracking/identify`, body, { timeout: 6000 });
    return r?.data?.person_id || null;
  } catch { return null; }
}

async function track(eventType, opts = {}) {
  if (!_hasConsent() || !eventType) return;
  const identity = _getIdentity();
  const payload = {
    visitor_id: _visitorId(),
    email: identity.email,
    user_id: identity.user_id,
    event_type: eventType.slice(0, 60),
    source: opts.source || 'web',
    ref_id: opts.ref_id,
    title: opts.title || document.title,
    url: opts.url || window.location.href,
    ..._getUtm(),
    metadata: opts.metadata,
  };
  try {
    // sendBeacon quando disponível (não bloqueia navegação)
    if (navigator.sendBeacon && !opts.awaitResponse) {
      const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
      const ok = navigator.sendBeacon(`${API}/api/tracking/event`, blob);
      if (ok) return;
    }
    await axios.post(`${API}/api/tracking/event`, payload, { timeout: 4000 });
  } catch { /* silencioso */ }
}

// Auto-pageview: dispara em cada mudança de URL do SPA (history.pushState)
let _lastPath = null;
function _sendPageview() {
  const path = window.location.pathname + window.location.search;
  if (path === _lastPath) return;
  _lastPath = path;
  track('page_view', { title: document.title, url: window.location.href });
}

function _installSPAHooks() {
  const origPush = window.history.pushState;
  const origReplace = window.history.replaceState;
  window.history.pushState = function (...args) {
    const r = origPush.apply(this, args);
    setTimeout(_sendPageview, 0);
    return r;
  };
  window.history.replaceState = function (...args) {
    const r = origReplace.apply(this, args);
    setTimeout(_sendPageview, 0);
    return r;
  };
  window.addEventListener('popstate', () => setTimeout(_sendPageview, 0));
}

let _started = false;
function start() {
  if (_started || typeof window === 'undefined') return;
  _started = true;
  _visitorId(); // garante cookie
  _installSPAHooks();
  _sendPageview();

  // Se o usuário já tem sessão local (token JWT), extrai email/user_id e faz identify
  try {
    const raw = localStorage.getItem('user');
    if (raw) {
      const u = JSON.parse(raw);
      if (u?.email || u?.id) {
        identify({
          email: u.email,
          user_id: u.id || u.user_id,
          display_name: u.name,
          company: u.company_name,
          role: u.role,
        });
      }
    }
  } catch {}
}

// Expõe para debugging via console
if (typeof window !== 'undefined') {
  window.personTracker = { identify, track, start, visitorId: _visitorId };
}

export default { identify, track, start, visitorId: _visitorId };
export { identify, track, start };
